import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  doctorPath,
  doctorPhoneShown,
  doctorShowOf,
  type Doctor,
  type DoctorShow,
  type DoctorSummary,
} from '@/domain';
import { useDoctorSummary, useDoctors } from '@/application/usecases/useClients';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { KpiCard, KpiGrid } from '@/ui/data/KpiCard';
import { Pagination } from '@/ui/data/Pagination';
import { ResponsiveTable, type Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { DoctorIcon, PlusIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { readFailure } from './clientRefusal';
import { CLIENT_SORT_PRESETS, clientSortOfPreset } from './clientSorts';
import { ContactConversionSheet } from './ContactConversionSheet';
import { DoctorFormSheet, WarmDoctorForm } from './DoctorFormSheet';
import { useListAddress } from './useListAddress';
import styles from './ClientsList.module.css';

/** The counts that are cards, after Total, in the order they are drawn. */
const CARDS = ['newThisWeek', 'newThisMonth', 'returning', 'withActiveCases'] as const;

/** What each narrowing of the list is called: on its card, and beside the count while it is on. */
const SHOW_LABEL: Record<DoctorShow, string> = {
  newThisWeek: strings.kpi.newThisWeek,
  newThisMonth: strings.kpi.newThisMonth,
  returning: strings.kpi.returning,
  withActiveCases: strings.kpi.withActiveCases,
  needsReview: strings.kpi.needsReview,
};

/**
 * Doctors. The request of 6 October 2026 (second), items 4 and 5.
 *
 * Every doctor the lab works for, as a record: the name, the clinic, the phone,
 * how many cases are in hand out of how many there have ever been, and how many
 * patients. A row opens the doctor's profile, which is where the rest is.
 *
 * Searched and ordered by the SERVER, a page at a time, as the archive is: the
 * list is every doctor the lab has ever had a case from, and narrowing or
 * ordering the page in hand would narrow or order twenty-five rows of it. The
 * search follows the typing after a pause, so a name is one request and not
 * one per letter, and the order a reader chose is remembered on the device.
 *
 * Add doctor is there for whoever may change the records (`manageClients`);
 * everybody else who holds the screen reads.
 *
 * Since the request of 6 October 2026 (third): each row leads with the
 * doctor's ID, which the search also finds (`DR-0012`, `dr 12`, or the number
 * alone); and Possible duplicates narrows the list to the doctors whose name
 * another doctor's differs from only in a title or in punctuation, each group
 * together. That list has one order, the server's, so the order control stands
 * down while it is on. Merging is done from a doctor's profile.
 *
 * Since the request of 6 October 2026 (fifth):
 *
 *   counts    five of them above the filter bar, the server's, on the lab's
 *             calendar: Total, New this week, New this month, Returning, With
 *             active cases. Each is a card to press: it narrows the list to
 *             exactly the doctors it counts (`show`), and pressing it again,
 *             pressing Total, or Clear filter beside the count widens it.
 *             Total is the pressed one while nothing narrows.
 *   address   the search, the count the list is narrowed to, the possible
 *             duplicates, the order and the page are in the address
 *             (`useListAddress`), so Back from a doctor finds the list as it
 *             was left.
 *   notices   to whoever holds `admin`, how many doctors still have a number
 *             or an address as free text, with the way to read what converting
 *             them would do and to do it; to whoever may change the records,
 *             how many the conversion left for a person, with the way to list
 *             them.
 *   phone     the column is the number in its international form, or the old
 *             text where there is no such number yet.
 */
export function DoctorsScreen() {
  const navigate = useNavigate();
  const session = useSession();
  const permissions = session.data?.permissions ?? [];
  const mayManage = permissions.includes('manageClients');
  const mayConvert = permissions.includes('admin');

  const address = useListAddress();
  const order = useTableOrder<Doctor>('doctors', CLIENT_SORT_PRESETS);
  // The size of a page is the device's to remember; which page it is on is the address's.
  const paging = useTablePage('doctors');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('doctors');
  const [adding, setAdding] = useState(false);
  const [converting, setConverting] = useState(false);

  const { q, duplicates, page } = address;
  // A word in the address that is no count of this list narrows nothing.
  const show = duplicates ? null : doctorShowOf(address.show);
  // The order the address names, when it is one of the four; otherwise the one the device remembers.
  const presetKey =
    CLIENT_SORT_PRESETS.find((preset) => preset.key === address.sort)?.key ?? order.presetKey;
  const sort = clientSortOfPreset(presetKey);

  const summary = useDoctorSummary();
  const counts = summary.data;
  // A count that could not be read is an empty card, not one that waits for ever.
  const countOf = (key: keyof DoctorSummary) => (summary.isError ? null : counts?.[key]);

  const doctors = useDoctors(
    useMemo(
      () => ({
        q,
        // The possible duplicates come grouped, in the server's one order: no `sort` goes with
        // them, and no `show`, which the server would not read.
        ...(duplicates ? { duplicates: true } : { sort, ...(show === null ? {} : { show }) }),
        page,
        pageSize: paging.pageSize,
      }),
      [q, duplicates, sort, show, page, paging.pageSize],
    ),
  );
  const { data, isPending, isError, isPlaceholderData } = doctors;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  // A page past the end, from an address older than the list: the last page there is.
  const { setPage } = address;
  useEffect(() => {
    if (data === undefined || isPlaceholderData) return;
    if (data.items.length === 0 && data.total > 0 && page > 1) {
      setPage(Math.ceil(data.total / paging.pageSize));
    }
  }, [data, isPlaceholderData, page, paging.pageSize, setPage]);

  function chooseOrder(key: string) {
    order.choose(key);
    // The first of the four is what the address means by naming none.
    address.setSort(key === CLIENT_SORT_PRESETS[0]?.key ? null : key);
  }

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.clients.searchDoctors,
      placeholder: strings.clients.searchDoctorsPlaceholder,
      value: address.text,
      onChange: address.setText,
    },
  ];

  const unset = <span className={styles.unset}>{strings.clients.notSet}</span>;

  const columns: Column<Doctor>[] = [
    {
      // The ID leads the row, in the face the job order number is set in.
      key: 'code',
      label: strings.clients.colId,
      mono: true,
      nowrap: true,
      render: (doctor) => doctor.code,
    },
    {
      key: 'name',
      label: strings.clients.colName,
      render: (doctor) => <span className={styles.name}>{doctor.name}</span>,
    },
    {
      key: 'clinic',
      label: strings.clients.colClinic,
      render: (doctor) =>
        doctor.clinic === null ? unset : <span className={styles.cell}>{doctor.clinic}</span>,
    },
    {
      // Plain text, not a link: the row is the thing to press, and the profile has the link.
      key: 'phone',
      label: strings.clients.colPhone,
      nowrap: true,
      // The number in its international form, or what was typed before there was one.
      render: (doctor) => doctorPhoneShown(doctor) ?? unset,
    },
    {
      key: 'cases',
      label: strings.clients.colCases,
      mono: true,
      nowrap: true,
      align: 'right',
      render: (doctor) => strings.clients.activeOfAll(doctor.activeCaseCount, doctor.caseCount),
    },
    {
      key: 'patients',
      label: strings.clients.colPatients,
      mono: true,
      align: 'right',
      render: (doctor) => doctor.patientCount,
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      key: 'email',
      label: strings.table.columns.email,
      optional: true,
      render: (doctor) =>
        doctor.email === null ? unset : <span className={styles.cell}>{doctor.email}</span>,
    },
    {
      key: 'website',
      label: strings.table.columns.website,
      optional: true,
      render: (doctor) =>
        doctor.website === null ? unset : <span className={styles.cell}>{doctor.website}</span>,
    },
    {
      // From the address in its parts, and not while it is waiting for somebody to review it.
      key: 'city',
      label: strings.table.columns.city,
      optional: true,
      render: (doctor) =>
        doctor.addressNeedsReview ? unset : (doctor.address?.city ?? unset),
    },
    {
      key: 'country',
      label: strings.table.columns.country,
      optional: true,
      render: (doctor) =>
        doctor.addressNeedsReview
          ? unset
          : (doctor.address?.countryName ?? doctor.address?.country ?? unset),
    },
    {
      // When the newest of the doctor's cases was taken in.
      key: 'lastCase',
      label: strings.table.columns.lastCase,
      mono: true,
      nowrap: true,
      optional: true,
      render: (doctor) => (doctor.lastCaseAt === null ? unset : formatDay(doctor.lastCaseAt)),
    },
    {
      key: 'added',
      label: strings.table.columns.added,
      mono: true,
      nowrap: true,
      optional: true,
      render: (doctor) => formatDay(doctor.createdAt),
    },
  ];

  const clearFilter = (
    <Button onClick={() => address.setShow(null)}>{strings.kpi.clearFilter}</Button>
  );

  return (
    <div>
      <div className={styles.head}>
        <span className={styles.lbl}>{strings.clients.label}</span>
        <h2>{strings.clients.doctorsTitle}</h2>
        <span className={styles.hint}>{strings.clients.doctorsHint}</span>
        {mayManage && (
          <>
            <span className={styles.spacer} />
            <Button variant="primary" icon={<PlusIcon />} onClick={() => setAdding(true)}>
              {strings.clients.addDoctor}
            </Button>
          </>
        )}
      </div>

      <KpiGrid label={strings.kpi.doctorsLabel}>
        <KpiCard
          label={strings.kpi.total}
          value={countOf('total')}
          // Everybody: pressed while neither a count nor the possible duplicates narrows the list.
          pressed={show === null && !duplicates}
          onPress={() => address.setShow(null)}
        />
        {CARDS.map((card) => (
          <KpiCard
            key={card}
            label={SHOW_LABEL[card]}
            value={countOf(card)}
            pressed={show === card}
            onPress={() => address.setShow(show === card ? null : card)}
          />
        ))}
      </KpiGrid>

      {mayConvert && counts !== undefined && counts.oldFormat > 0 && (
        <p className={styles.notice} role="status">
          <span>{strings.clients.oldFormatNotice(counts.oldFormat)}</span>
          <Button onClick={() => setConverting(true)}>{strings.clients.reviewAndConvert}</Button>
        </p>
      )}
      {mayManage && counts !== undefined && counts.needsReview > 0 && (
        <p className={styles.notice} role="status">
          <span>{strings.clients.needsReviewNotice(counts.needsReview)}</span>
          {show !== 'needsReview' && (
            <Button onClick={() => address.setShow('needsReview')}>
              {strings.clients.showThem}
            </Button>
          )}
        </p>
      )}

      <FilterBar
        label={strings.clients.doctorFiltersLabel}
        filters={filters}
        // Not drawn while the possible duplicates are listed: their order is the
        // server's, and a control that changed nothing would say otherwise.
        sort={
          duplicates ? undefined : (
            <SortSelect presets={CLIENT_SORT_PRESETS} value={presetKey} onChange={chooseOrder} />
          )
        }
        actions={
          <button
            type="button"
            className={`${styles.toggle} ${duplicates ? styles.toggleOn : ''}`}
            aria-pressed={duplicates}
            onClick={() => address.setDuplicates(!duplicates)}
          >
            {strings.clients.duplicatesToggle}
          </button>
        }
      />

      {isError && (
        <p className={styles.error} role="alert">
          {readFailure(doctors.error)}
          <Button onClick={() => void doctors.refetch()}>{strings.errors.retry}</Button>
        </p>
      )}

      {isPending && <SkeletonRows rows={6} label={strings.clients.loadingDoctors} />}

      {!isPending && !isError && rows.length === 0 && (
        <EmptyState
          icon={<DoctorIcon />}
          heading={
            q !== ''
              ? strings.clients.noDoctorMatches
              : duplicates
                ? strings.clients.noDuplicates
                : show !== null
                  ? strings.clients.noDoctorsShown
                  : strings.clients.noDoctors
          }
          detail={
            q !== ''
              ? strings.clients.noMatchDetail
              : duplicates
                ? strings.clients.noDuplicatesDetail
                : show !== null
                  ? strings.clients.noShownDetail
                  : strings.clients.noDoctorsDetail
          }
          // The next step out of a count nobody is in.
          action={show === null ? undefined : clearFilter}
        />
      )}

      {!isPending && !isError && rows.length > 0 && (
        <>
          <div className={styles.count}>
            <b>{total.toLocaleString()}</b> {strings.clients.doctorsCounted(total)}
            {show !== null && (
              <span className={styles.narrowed}>
                <span>{strings.kpi.filterOn(SHOW_LABEL[show])}</span>
                <button
                  type="button"
                  className={styles.clearFilter}
                  onClick={() => address.setShow(null)}
                >
                  {strings.kpi.clearFilter}
                </button>
              </span>
            )}
          </div>
          <ResponsiveTable
            customize={{ layout: layout.layout, save: layout.save, reset: layout.reset, rows: { size: paging.pageSize, sizes: PAGE_SIZES, choose: paging.choose } }}
            columns={columns}
            rows={rows}
            rowKey={(doctor) => doctor.id}
            caption={strings.clients.doctorsCaption}
            onRowClick={(doctor) => void navigate(doctorPath(doctor.id))}
          />
          <Pagination
            page={page}
            pageSize={paging.pageSize}
            total={total}
            sizes={PAGE_SIZES}
            onPage={address.setPage}
            onPageSize={(size) => {
              paging.choose(size);
              address.setPage(1);
            }}
            label={strings.table.pagesLabel}
          />
        </>
      )}

      {/* The form's two lists, asked for once the doctors are on screen: the form then opens with both in hand. */}
      {mayManage && !isPending && <WarmDoctorForm />}

      {adding && (
        <DoctorFormSheet
          onClose={() => setAdding(false)}
          // Straight to the new record, where its ID is and where it is checked against any doctor it looks like.
          onDone={(doctor) => {
            setAdding(false);
            void navigate(doctorPath(doctor.id));
          }}
        />
      )}

      {converting && <ContactConversionSheet onClose={() => setConverting(false)} />}
    </div>
  );
}
